// Copyright ©️ 2026 eVolve MEP, LLC

import type { WrappedPaginatedGet, StrongOmit, EvolveApiRoute } from '@evolvemep/api-ts';

import {
  ToggleRadioGroup,
  type ToggleRadioGroupOption,
  type ToggleRadioGroupProps,
} from '../TypeSafeSelect/ToggleRadioGroup';

type Props<T, K extends string, Route extends EvolveApiRoute<'GET_PAGINATED'>> = StrongOmit<
  ToggleRadioGroupProps<T, K>,
  'data' | 'loading' | 'onVisible'
> & {
  paginatedGet: WrappedPaginatedGet<Route>;
  getOption: (item: WrappedPaginatedGet<Route>['data'][number]) => ToggleRadioGroupOption<T, K>;
};

/**
 * A ToggleRadioGroup component that supports paginated data fetching.
 */
export const PaginatedToggleRadioGroup = <T, K extends string, Route extends EvolveApiRoute<'GET_PAGINATED'>>({
  paginatedGet,
  getOption,
  ...props
}: Props<T, K, Route>) => (
  <ToggleRadioGroup
    data={paginatedGet.data.map(getOption)}
    {...props}
    loading={paginatedGet.loading}
    onVisible={paginatedGet.fetchNextPage}
  />
);
